import { useState, useEffect, useRef } from 'react';
import { X, ChevronDown } from 'lucide-react';

const HelpModal = ({ isOpen, onClose, onApplyExample }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [exampleApplied, setExampleApplied] = useState(false);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleApplyExample = () => {
    onApplyExample();
    setExampleApplied(true);
    setTimeout(() => {
      setExampleApplied(false);
      onClose();
    }, 1500);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content help-modal" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="help-title">
        <div className="modal-header">
          <h2 id="help-title" className="modal-title">كيفية الاستخدام</h2>
          <button 
            ref={closeButtonRef}
            className="modal-close" 
            onClick={onClose}
            aria-label="إغلاق"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="modal-body help-modal-body">
          {/* Section A: Practical Example */}
          <div className="help-section">
            <button 
              className="help-section-header"
              onClick={() => toggleSection('example')}
              aria-expanded={expandedSection === 'example'}
            >
              <span className="help-section-title">مثال عملي (فاتورة مطعم)</span>
              <ChevronDown 
                size={20} 
                className={`chevron ${expandedSection === 'example' ? 'expanded' : ''}`}
              />
            </button>
            
            {expandedSection === 'example' && (
              <div className="help-section-content">
                <ol className="help-steps">
                  <li>أدخل المبلغ المطلوب واختر العملة</li>
                  <li>أدخل المبلغ المدفوع (يمكن إضافة دفعة ثانية)</li>
                  <li>اضغط "احسب" لرؤية الباقي أو الناقص</li>
                </ol>
                
                <button 
                  className="btn-primary btn-try-example"
                  onClick={handleApplyExample}
                >
                  جرّب المثال
                </button>
                
                {exampleApplied && (
                  <div className="example-applied-message">
                    تم تعبئة المثال
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section B: Installation Instructions */}
          <div className="help-section">
            <button 
              className="help-section-header"
              onClick={() => toggleSection('install')}
              aria-expanded={expandedSection === 'install'}
            >
              <span className="help-section-title">تثبيت التطبيق على الهاتف</span>
              <ChevronDown 
                size={20} 
                className={`chevron ${expandedSection === 'install' ? 'expanded' : ''}`}
              />
            </button>
            
            {expandedSection === 'install' && (
              <div className="help-section-content">
                <div className="install-instructions">
                  <div className="install-step">
                    <strong>iPhone (Safari):</strong>
                    <p>⤴️ ثم "Add to Home Screen" ➕ ثم "Add"</p>
                  </div>
                  <div className="install-step">
                    <strong>Android (Chrome):</strong>
                    <p>⋮ ثم "Add to Home screen" أو "Install app" ثم "Install"</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
